import {
  advanceGoldTo,
  applyGoldInput,
  GOLD_V2_TICKS,
  type GoldInput,
  type GoldV2State,
} from '@wikigold/engine';
import { hookPose, poseFromTip } from '../components/hook-motion';
import { boundedTravelPoint, correctedPoint, correctionForJump } from './visual-smoothing';

const TICKS_PER_SECOND = 30;
const MAX_PREDICTION_TICKS = TICKS_PER_SECOND;
const MAX_FRAME_TICKS = TICKS_PER_SECOND;

export interface GoldRunView {
  id: string;
  dateKey: string;
  phase: GoldV2State['phase'];
  state: GoldV2State;
  goldAmount: number;
  startedAt: string;
  dayEndAt: string;
  serverNow: string;
  clockFrozen: boolean;
  replayVerified: boolean;
  version: number;
  testMock: boolean;
}

function serverTick(run: GoldRunView): number {
  if (run.clockFrozen || run.state.phase !== 'RUNNING') return run.state.tick;
  const elapsed = (Date.parse(run.serverNow) - Date.parse(run.startedAt)) * TICKS_PER_SECOND / 1000;
  return Number.isFinite(elapsed)
    ? Math.max(run.state.tick, Math.min(elapsed, run.state.dayEndTick, GOLD_V2_TICKS))
    : run.state.tick;
}

/** Authoritative state + unacknowledged input replay; smoothing affects pose only. */
export class GoldTimeline {
  confirmed: GoldRunView;
  cursor: GoldV2State;
  previous: GoldV2State;
  frozen: boolean;
  pending: GoldInput[] = [];
  needsResync = false;
  private anchorTick: number;
  private anchorAt: number;
  private sampledAt: number;
  private lastPose: ReturnType<typeof hookPose> | null = null;
  private presentedShot = -1;
  private presentedStatus = '';
  private correction = { x: 0, y: 0 };
  private correctionAt = 0;

  constructor(initial: GoldRunView, now: number) {
    this.confirmed = initial;
    this.frozen = initial.clockFrozen;
    this.cursor = initial.state;
    this.previous = this.cursor;
    this.anchorTick = serverTick(initial);
    this.anchorAt = now;
    this.sampledAt = now;
    this.advance(this.targetTick(now));
  }

  private projectedTick(now: number): number {
    return this.frozen ? this.confirmed.state.tick
      : this.anchorTick + Math.max(0, now - this.anchorAt) * TICKS_PER_SECOND / 1000;
  }

  private targetTick(now: number): number {
    const projected = Math.min(Math.floor(this.projectedTick(now) + 1e-8), this.cursor.dayEndTick, GOLD_V2_TICKS);
    const cap = this.confirmed.state.tick + MAX_PREDICTION_TICKS;
    if (!this.frozen && this.confirmed.state.phase === 'RUNNING' && projected > cap) this.needsResync = true;
    return Math.max(this.confirmed.state.tick, Math.min(projected, cap));
  }

  private advance(target: number): void {
    while (this.cursor.tick < target && this.cursor.phase === 'RUNNING') {
      this.previous = this.cursor;
      this.cursor = advanceGoldTo(this.cursor, this.cursor.tick + 1, this.cursor.dayEndTick).state;
    }
  }

  sample = (now: number) => {
    // A RAF timestamp can precede a network task processed in the same frame.
    now = Math.max(this.sampledAt, now);
    this.sampledAt = now;
    const desired = this.targetTick(now);
    // 10/20fps and short stalls consume every elapsed fixed tick. Long gaps
    // spend at most one second of simulation, then fetch a fresh checkpoint.
    if (desired > this.cursor.tick + MAX_FRAME_TICKS) this.needsResync = true;
    this.advance(Math.min(desired, this.cursor.tick + MAX_FRAME_TICKS));

    const progress = this.frozen || this.cursor.phase !== 'RUNNING'
      ? 1 : Math.max(0, Math.min(1, this.projectedTick(now) - this.cursor.tick));
    const pose = hookPose(this.previous, this.cursor, progress);
    let tip = pose.tip;
    const sameShot = this.presentedShot === this.cursor.shots;
    const sameStatus = this.presentedStatus === this.cursor.hook.status;
    if (this.lastPose && sameShot && sameStatus && !this.frozen && this.cursor.phase === 'RUNNING') {
      tip = correctedPoint(tip, this.correction, now - this.correctionAt);
      // Only the rope presentation may wait for the corrected path to catch
      // up. Objects, carry, input eligibility and terminal state never do.
      tip = boundedTravelPoint(this.lastPose.tip, tip, pose.origin, this.cursor.hook.status);
    } else {
      this.correction = { x: 0, y: 0 };
    }
    const presented = poseFromTip(tip, this.cursor);
    this.lastPose = presented;
    this.presentedShot = this.cursor.shots;
    this.presentedStatus = this.cursor.hook.status;
    return {
      at: now,
      frozen: this.frozen,
      // A predicted catch may be corrected. Credit and its success notice
      // must wait for the server, rather than being awarded and clawed back.
      state: this.cursor.goldAmount === this.confirmed.state.goldAmount
        ? this.cursor : { ...this.cursor, goldAmount: this.confirmed.state.goldAmount },
      pose: presented,
    };
  };

  receive(run: GoldRunView, now: number, reconnected = false): boolean {
    if (run.id !== this.confirmed.id) return false;
    if (
      run.version < this.confirmed.version ||
      run.state.tick < this.confirmed.state.tick ||
      run.state.lastSeq < this.confirmed.state.lastSeq ||
      (this.confirmed.state.phase !== 'RUNNING' && run.state.phase === 'RUNNING')
    ) {
      // An HTTP resync can beat the socket's ready packet. Its newer state
      // still wins, but old unconfirmed clicks must not survive reconnection.
      if (reconnected) this.discardPending(now);
      return false;
    }

    now = Math.max(this.sampledAt, now);
    const projected = this.projectedTick(now);
    const wasFrozen = this.frozen;
    this.confirmed = run;
    this.frozen = run.clockFrozen;
    // A ready packet is read after acquiring server controller ownership.
    // Accepted old input is in this snapshot; unconfirmed offline input is
    // discarded, never backfilled at a later receipt tick.
    this.pending = reconnected ? [] : this.pending.filter(input => input.seq > run.state.lastSeq);
    this.needsResync = false;
    this.anchorTick = this.frozen || wasFrozen ? serverTick(run) : Math.max(serverTick(run), projected);
    this.anchorAt = now;
    this.sampledAt = now;

    if (run.state.phase !== 'RUNNING') {
      // A terminal checkpoint wins even over a locally predicted later tick.
      this.pending = [];
      this.cursor = run.state;
      this.previous = this.cursor;
      this.lastPose = null;
      this.correction = { x: 0, y: 0 };
      return true;
    }

    this.rebuild(this.targetTick(now), now);
    return true;
  }

  input() {
    if (
      this.cursor.phase !== 'RUNNING' ||
      this.cursor.hook.status !== 'SWINGING' ||
      this.pending.length > 0 || this.needsResync
    ) return null;
    const input: GoldInput = {
      seq: this.confirmed.state.lastSeq + 1,
      tick: this.cursor.tick,
      type: 'release',
    };
    const predicted = applyGoldInput(this.cursor, input, this.cursor.dayEndTick);
    if (!predicted.accepted) return null;
    this.pending.push(input);
    this.previous = this.cursor;
    this.cursor = predicted.state;
    // The server assigns the receipt tick; the local tick is prediction only.
    return { seq: input.seq, type: input.type };
  }

  discardPending(now: number): void {
    if (this.pending.length === 0) return;
    now = Math.max(this.sampledAt, now);
    this.pending = [];
    this.rebuild(this.targetTick(now), now);
  }

  private rebuild(target: number, now: number): void {
    this.cursor = this.confirmed.state;
    this.previous = this.cursor;
    for (const input of this.pending) {
      const tick = Math.min(target, Math.max(this.cursor.tick, input.tick));
      this.advance(tick);
      const replayed = applyGoldInput(this.cursor, { ...input, tick }, this.cursor.dayEndTick);
      if (replayed.accepted) {
        this.cursor = replayed.state;
        this.previous = this.cursor;
      }
    }
    // target is capped to checkpoint + 30, including replay work above.
    this.advance(target);
    if (this.lastPose && !this.frozen && this.presentedShot === this.cursor.shots && this.presentedStatus === this.cursor.hook.status) {
      const progress = Math.max(0, Math.min(1, this.projectedTick(now) - this.cursor.tick));
      this.correction = correctionForJump(this.lastPose.tip, hookPose(this.previous, this.cursor, progress).tip);
      this.correctionAt = now;
    } else {
      this.correction = { x: 0, y: 0 };
      this.lastPose = null;
    }
  }
}
